class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.ocScrollMode = window.OCFilterScroll?.hasFilterParams(searchParams) ? 'jump' : 'restore';
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.captureMobileDrawerState(event);
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    if (FacetFiltersForm.ocScrollMode === 'restore') window.OCFilterScroll?.restoreImmediately();
  }

  static captureMobileDrawerState(event) {
    const target = event && event.target;
    const mobileForm = target && target.closest ? target.closest('#FacetFiltersFormMobile') : null;
    if (!mobileForm) {
      FacetFiltersForm.mobileDrawerState = null;
      return;
    }

    const main = mobileForm.querySelector('.mobile-facets__main');
    const inner = mobileForm.querySelector('.mobile-facets__inner');
    const genreList = mobileForm.querySelector('.oc-mobile-genre-values');
    FacetFiltersForm.mobileDrawerState = {
      mainScrollTop: main ? main.scrollTop : 0,
      innerScrollTop: inner ? inner.scrollTop : 0,
      genreScrollTop: genreList ? genreList.scrollTop : 0,
      windowScrollY: window.scrollY,
    };
  }

  static restoreMobileDrawerState() {
    const state = FacetFiltersForm.mobileDrawerState;
    if (!state) return;

    requestAnimationFrame(() => {
      const mobileForm = document.getElementById('FacetFiltersFormMobile');
      if (!mobileForm) return;
      const main = mobileForm.querySelector('.mobile-facets__main');
      const inner = mobileForm.querySelector('.mobile-facets__inner');
      const genreList = mobileForm.querySelector('.oc-mobile-genre-values');
      if (main) main.scrollTop = state.mainScrollTop;
      if (inner) inner.scrollTop = state.innerScrollTop;
      if (genreList) genreList.scrollTop = state.genreScrollTop;
      // AJAX filtering should never move the page itself while the drawer is open.
      if (Math.abs(window.scrollY - state.windowScrollY) > 2) {
        window.scrollTo({ top: state.windowScrollY, left: window.scrollX, behavior: 'auto' });
      }
      FacetFiltersForm.mobileDrawerState = null;
    });
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.restoreMobileDrawerState();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.restoreMobileDrawerState();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    const alreadyRestored = FacetFiltersForm.ocScrollMode === 'restore' && window.OCFilterScroll?.isRestoredImmediately();
    if (!alreadyRestored) {
      const restoredPage = FacetFiltersForm.ocScrollMode === 'restore'
        ? window.OCFilterScroll?.restoreBrowseGrid(container) : 0;
      if (!restoredPage) {
        container.innerHTML = new DOMParser()
          .parseFromString(html, 'text/html')
          .getElementById('ProductGridContainer').innerHTML;
      }
      window.OCLoadMore?.onGridReplaced(restoredPage);
    }

    container.querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    if (FacetFiltersForm.ocScrollMode === 'restore') window.OCFilterScroll?.restoreAfterAjax();
    else if (FacetFiltersForm.ocScrollMode === 'jump') window.OCFilterScroll?.jumpAfterAjax();
    FacetFiltersForm.ocScrollMode = null;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const desktopFacetWrapper = document.getElementById('FacetsWrapperDesktop');
    const desktopFacetScrollTop = desktopFacetWrapper ? desktopFacetWrapper.scrollTop : 0;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const isMobileSortEvent = Boolean(
      event &&
        event.target &&
        event.target.closest &&
        event.target.closest('#FacetFiltersFormMobile') &&
        (event.target.id === 'SortBy-mobile' || event.target.classList.contains('oc-mobile-sort-native'))
    );

    // Remove facets that are no longer returned from the server. A sort-only
    // refresh must never destroy the open mobile genre/filter list.
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (isMobileSortEvent && currentElement.closest('#FacetFiltersFormMobile')) return;
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
      (element) => !matchesId(element) && !(isMobileSortEvent && element.closest('#FacetFiltersFormMobile'))
    );
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender) => {
      const currentElement = document.getElementById(elementToRender.id);

      // Element already rendered in the DOM, so update it in place.
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
        return;
      }

      // A selected facet can cause Shopify to omit other facet groups from an
      // AJAX response. Dawn removes those groups above. When filters are later
      // cleared, put every returned group back in its original server order.
      // The stock fallback assumes another .js-filter still exists; if all
      // groups were removed it can fail, leaving the mobile drawer empty until
      // a hard refresh.
      const sourceParent = elementToRender.parentElement;
      const liveParent = sourceParent && sourceParent.id ? document.getElementById(sourceParent.id) : null;
      if (!sourceParent || !liveParent) return;

      const sourceSiblings = Array.from(sourceParent.children);
      const sourceIndex = sourceSiblings.findIndex((node) => node.id === elementToRender.id);
      const elementClone = elementToRender.cloneNode(true);

      // Prefer the next live sibling so ordering exactly matches the response.
      for (let i = sourceIndex + 1; i < sourceSiblings.length; i += 1) {
        const nextId = sourceSiblings[i].id;
        if (!nextId) continue;
        const nextLive = document.getElementById(nextId);
        if (nextLive && nextLive.parentElement === liveParent) {
          liveParent.insertBefore(elementClone, nextLive);
          return;
        }
      }

      // Otherwise place it after the closest preceding live sibling.
      for (let i = sourceIndex - 1; i >= 0; i -= 1) {
        const previousId = sourceSiblings[i].id;
        if (!previousId) continue;
        const previousLive = document.getElementById(previousId);
        if (previousLive && previousLive.parentElement === liveParent) {
          previousLive.after(elementClone);
          return;
        }
      }

      // No facet siblings remain. Insert before sort/footer controls when
      // possible, otherwise append safely.
      const fallbackAnchor = liveParent.querySelector(
        '[data-index="mobile-sort"], .mobile-facets__footer, .facets__footer, .facet-filters, .product-count'
      );
      if (fallbackAnchor) liveParent.insertBefore(elementClone, fallbackAnchor);
      else liveParent.appendChild(elementClone);
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    requestAnimationFrame(() => {
      const wrapper = document.getElementById('FacetsWrapperDesktop');
      if (wrapper) wrapper.scrollTop = desktopFacetScrollTop;
      window.OCMobileActiveFacetsSync?.();
      window.OCAlignDesktopFacetPanel?.();
    });

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';
        const isMobileCheckbox = event.target.classList && event.target.classList.contains('mobile-facets__checkbox');

        // Do not refocus the rebuilt mobile facet after every checkbox tap.
        // On touch devices that focus call scrolls the drawer back to the top.
        if (newElementToActivate && !isTextInput && !isMobileCheckbox) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const liveActiveFacetsElement = document.querySelector(selector);
      if (!activeFacetsElement || !liveActiveFacetsElement) return;
      liveActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sourceForm = event.target && event.target.closest ? event.target.closest('form') : null;

    // Mobile is intentionally staged: checkbox/sort changes stay local until
    // the user taps APPLY. This prevents Dawn's AJAX renderer from removing
    // filter groups and from rebuilding the drawer while the user is scrolling.
    if (sourceForm && sourceForm.id === 'FacetFiltersFormMobile') return;

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    sortFilterForms.forEach((form) => {
      if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
        forms.push(this.createSearchParams(form));
      }
    });
    const nextParams = forms.join('&');
    const changedFilter = Boolean(event.target && event.target.name && event.target.name.startsWith('filter.'));
    if (changedFilter) {
      if (window.OCFilterScroll?.hasFilterParams(nextParams)) {
        window.OCFilterScroll.saveBrowsePosition();
        FacetFiltersForm.ocScrollMode = 'jump';
      } else {
        FacetFiltersForm.ocScrollMode = 'restore';
      }
    }
    this.onSubmitForm(nextParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    if (window.OCFilterScroll?.hasFilterParams(url)) {
      window.OCFilterScroll.saveBrowsePosition();
      FacetFiltersForm.ocScrollMode = 'jump';
    } else {
      FacetFiltersForm.ocScrollMode = 'restore';
    }
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.mobileDrawerState = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


/* OMNI-CULT MOBILE ACTIVE FACETS REHYDRATION */
(() => {
  if (window.__ocMobileActiveFacetsRehydration) return;
  window.__ocMobileActiveFacetsRehydration = true;

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  const buildUrlWithoutOne = (params, removeName, removeValue) => {
    const next = new URLSearchParams();
    let removed = false;
    params.forEach((value, name) => {
      if (!removed && name === removeName && value === removeValue) {
        removed = true;
        return;
      }
      next.append(name, value);
    });
    next.delete('page');
    const query = next.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  };

  const buildClearAllUrl = (params) => {
    const next = new URLSearchParams();
    params.forEach((value, name) => {
      if (!name.startsWith('filter.')) next.append(name, value);
    });
    next.delete('page');
    const query = next.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  };

  const findLabel = (form, name, value) => {
    if (!form) return value;
    const inputs = Array.from(form.querySelectorAll('[name]'));
    const input = inputs.find((node) => node.name === name && String(node.value) === String(value));
    if (!input) return value;

    let label = null;
    if (input.id) {
      try { label = form.querySelector(`label[for="${CSS.escape(input.id)}"]`); } catch (_) {}
    }
    if (!label) label = input.closest('label');

    const explicit = label && label.querySelector('.facet-checkbox__text-label');
    const text = (explicit?.textContent || label?.textContent || value).replace(/\s+/g, ' ').trim();
    return text || value;
  };

  window.OCMobileActiveFacetsSync = () => {
    if (!isMobile()) return;

    const host = document.querySelector('.active-facets-mobile');
    const form = document.getElementById('FacetFiltersFormMobile');
    if (!host) return;

    const params = new URLSearchParams(window.location.search);
    const activePairs = Array.from(params.entries()).filter(([name]) => name.startsWith('filter.'));

    if (!activePairs.length) {
      host.classList.remove('oc-mobile-active-facets-force');
      return;
    }

    const existingChips = host.querySelectorAll('a.active-facets__button').length;
    const existingRemoveAll = host.querySelector('a.active-facets__button-remove');

    if (existingChips > 0 && existingRemoveAll) {
      host.classList.add('oc-mobile-active-facets-force');
      return;
    }

    const removeAllLabel =
      existingRemoveAll?.textContent?.trim() ||
      (window.location.pathname.includes('/collections/see-all-our-pins-here') ? 'ALL BUTTONS' : 'REMOVE ALL FILTERS');

    host.innerHTML = '';

    activePairs.forEach(([name, value]) => {
      const wrapper = document.createElement('facet-remove');

      const link = document.createElement('a');
      link.href = buildUrlWithoutOne(params, name, value);
      link.className = 'active-facets__button active-facets__button--light';

      const inner = document.createElement('span');
      inner.className = 'active-facets__button-inner button button--tertiary';
      inner.appendChild(document.createTextNode(findLabel(form, name, value)));

      const icon = document.createElement('span');
      icon.className = 'svg-wrapper';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '×';

      const hidden = document.createElement('span');
      hidden.className = 'visually-hidden';
      hidden.textContent = 'Clear filter';

      inner.appendChild(icon);
      inner.appendChild(hidden);
      link.appendChild(inner);
      wrapper.appendChild(link);
      host.appendChild(wrapper);
    });

    const clearWrapper = document.createElement('facet-remove');
    clearWrapper.className = 'active-facets__button-wrapper';

    const clearLink = document.createElement('a');
    clearLink.href = buildClearAllUrl(params);
    clearLink.className = 'active-facets__button-remove underlined-link';

    const clearText = document.createElement('span');
    clearText.textContent = removeAllLabel;

    clearLink.appendChild(clearText);
    clearWrapper.appendChild(clearLink);
    host.appendChild(clearWrapper);

    host.classList.add('oc-mobile-active-facets-force');
  };

  const start = () => window.OCMobileActiveFacetsSync();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('popstate', start);
})();


/* OMNI-CULT desktop facet/card vertical alignment */
(() => {
  if (window.__ocFacetAlignStableV2) return;
  window.__ocFacetAlignStableV2 = true;

  const desktopMq = window.matchMedia('(min-width: 750px)');
  let alignFrame = 0;
  let resizeObserver = null;
  let settleTimer = 0;

  const getFirstCard = () =>
    document.querySelector('#product-grid.product-grid > .grid__item:not(.oc-buttons-no-results) > .card-wrapper.product-card-wrapper') ||
    document.querySelector('#product-grid.product-grid > .grid__item:not(.oc-buttons-no-results) > .product-card-wrapper') ||
    document.querySelector('#product-grid.product-grid > .grid__item:not(.oc-buttons-no-results) > .card-wrapper') ||
    document.querySelector('#product-grid.product-grid > .grid__item:not(.oc-buttons-no-results)');

  const alignNow = () => {
    const aside = document.getElementById('main-collection-filters');
    if (!aside) return;

    if (!desktopMq.matches) {
      aside.style.removeProperty('--oc-facet-card-align-offset');
      document.documentElement.style.removeProperty('--oc-filter-border-top');
      aside.classList.remove('oc-facet-measuring');
      aside.classList.add('oc-facet-aligned');
      return;
    }

    if (document.body.classList.contains('collection-see-all-our-pins-here')) {
      aside.style.setProperty('--oc-facet-card-align-offset', '0px');
      aside.classList.remove('oc-facet-measuring');
      aside.classList.add('oc-facet-aligned');
      return;
    }

    const shell = aside.querySelector('#FacetFiltersForm');
    const firstCard = getFirstCard();
    if (!shell || !firstCard) {
      aside.classList.add('oc-facet-aligned');
      return;
    }

    // Always measure both elements in their natural document-flow positions.
    aside.classList.add('oc-facet-measuring');
    aside.style.setProperty('--oc-facet-card-align-offset', '0px');

    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    const shellDocumentTop = shell.getBoundingClientRect().top + scrollY;
    const cardDocumentTop = firstCard.getBoundingClientRect().top + scrollY;
    const offset = Math.round(cardDocumentTop - shellDocumentTop);

    aside.style.setProperty('--oc-facet-card-align-offset', `${offset}px`);
    aside.classList.remove('oc-facet-measuring');
    aside.classList.add('oc-facet-aligned');
  };


  const schedule = () => {
    cancelAnimationFrame(alignFrame);
    alignFrame = requestAnimationFrame(() => {
      requestAnimationFrame(alignNow);
    });
  };

  const settle = () => {
    schedule();
    clearTimeout(settleTimer);
    settleTimer = window.setTimeout(schedule, 120);
    window.setTimeout(schedule, 350);
    window.setTimeout(schedule, 800);
  };

  window.OCAlignDesktopFacetPanel = settle;

  const observe = () => {
    resizeObserver?.disconnect();
    if (!window.ResizeObserver) return;

    const gridContainer = document.getElementById('ProductGridContainer');
    const productGrid = document.getElementById('product-grid');
    const aside = document.getElementById('main-collection-filters');

    resizeObserver = new ResizeObserver(() => schedule());
    [gridContainer, productGrid, aside].forEach((node) => {
      if (node) resizeObserver.observe(node);
    });
  };

  const start = () => {
    settle();
    observe();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => settle()).catch(() => {});
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('load', settle, { once: true });
  window.addEventListener('resize', settle, { passive: true });
  desktopMq.addEventListener?.('change', settle);

  document.addEventListener('shopify:section:load', () => {
    start();
  });
})();
